import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { ClaimedVia } from "../invitation.types";

export type ClaimPendingInvitationArgs = {
  shortcode: string;
  claimedVia?: ClaimedVia;
};

export type ClaimPendingInvitationResponse = {
  program_id: number;
  invitation_record_id: number;
};

/**
 * Hook for claiming a pending invitation from the dashboard.
 *
 * Unlike useClaimInvitation, this does NOT navigate after claiming.
 * Instead it invalidates relevant queries so the dashboard updates.
 *
 * @example
 * ```tsx
 * const { mutate: claim, isPending } = useClaimPendingInvitation();
 *
 * const handleClaim = (shortcode: string) => {
 *   claim({ shortcode }, {
 *     onSuccess: () => { ... },
 *   });
 * };
 * ```
 */
export function useClaimPendingInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shortcode,
      claimedVia = "direct_link",
    }: ClaimPendingInvitationArgs): Promise<ClaimPendingInvitationResponse> => {
      return apiRequest(`/invitation/${shortcode}/claim/`, "POST", {
        claimed_via: claimedVia,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["pendingInvitations"] }),
        queryClient.invalidateQueries({ queryKey: ["actions"] }),
        queryClient.invalidateQueries({ queryKey: ["programs"] }),
        queryClient.invalidateQueries({ queryKey: ["userPrograms"] }),
        queryClient.invalidateQueries({ queryKey: ["affiliationRoles"] }),
        queryClient.invalidateQueries({ queryKey: ["organizations"] }),
      ]);
    },
  });
}
